<template>
  <div class="col-md-6 mb-3">
    <div class="controller-card card-item">
      <!-- 헤더: 모델명과 상태 배지 -->
      <div class="controller-card-header">
        <div class="controller-header-left">
          <i :class="controllerIconClass" class="controller-icon"></i>
          <div class="controller-title-block">
            <span class="controller-model">{{ modelName }}</span>
            <span v-if="props.data?._id" class="controller-serial">{{ formatControllerSerialWithName(props.data) }}</span>
            <div v-if="roomNames.length > 0" class="controller-room-tags">
              <span
                v-for="name in roomNames"
                :key="name"
                class="controller-room-tag"
              >{{ name }}</span>
            </div>
          </div>
        </div>
        <div class="controller-header-right">
          <ControllerStatus 
            :controller-id="props.data._id" 
            :auto-check="false"
            @status-changed="onStatusChanged"
          />
        </div>
      </div>

      <!-- 본문: 컨트롤러 정보 -->
      <div class="controller-card-body">
      <!-- 펌웨어 버전 정보 -->
      <div class="controller-info-item" v-if="hasValidVersion">
        <div class="controller-info-line">
          <i class="fa-solid fa-code-branch me-2"></i>
          <span class="controller-info-label">현재 펌웨어 버전:</span>
          <span class="controller-info-value">v{{ props.data.version }}</span>
          <span
            v-if="props.data.lastVersion && props.data.version"
            class="status-badge status-badge-inline"
            :class="needsUpdate ? 'status-badge-update' : 'status-badge-current'"
          >
            <i
              v-if="needsUpdate"
              class="fa-solid fa-circle-exclamation me-1"
            ></i>
            <i
              v-else
              class="fa-regular fa-circle-check me-1"
            ></i>
            {{ needsUpdate ? '업데이트 필요' : '최신 버전' }}
          </span>
        </div>
      </div>

      <!-- 최종 펌웨어 버전 정보 -->
      <div class="controller-info-item" v-if="needsUpdate">
        <div class="controller-info-line">
          <i class="fa-solid fa-code-branch me-2"></i>
          <span class="controller-info-label">최종 펌웨어 버전:</span>
          <span class="controller-info-value">v{{ props.data.lastVersion }}</span>
        </div>
      </div>

      <!-- SSID -->
      <div class="controller-info-item">
        <div class="controller-info-line">
          <i class="fa-solid fa-network-wired me-2"></i>
          <span class="controller-info-label">SSID:</span>
          <span class="controller-info-value">
            <span v-if="hasSsid">{{ props.data.ssid }}</span>
            <span v-else class="controller-info-no-data">정보 없음</span>
          </span>
        </div>
      </div>

      <!-- WiFi 신호세기 -->
      <div class="controller-info-item">
        <div class="controller-info-line">
          <i class="fa-solid fa-wifi me-2" :style="{ color: showSignalUnknown ? '#6c757d' : (hasRssi ? signalStrength.color : '#6c757d') }"></i>
          <span class="controller-info-label">WiFi 신호세기:</span>
          <span class="controller-info-value">
            <span v-if="showSignalUnknown" class="controller-info-no-data">알 수 없음</span>
            <div v-else-if="hasRssi" class="signal-strength-container">
              <div class="signal-bars">
                <span
                  v-for="i in 10"
                  :key="i"
                  class="signal-bar"
                  :class="{ 'signal-bar-active': i <= signalBars }"
                  :style="{
                    backgroundColor: i <= signalBars ? signalStrength.color : '#e0e0e0'
                  }"
                ></span>
              </div>
              <span
                class="signal-strength-badge"
                :style="{
                  backgroundColor: signalStrength.color + '20',
                  color: signalStrength.color,
                  borderColor: signalStrength.color
                }"
              >
                {{ signalStrength.label }}
              </span>
              {{ props.data?.rssi }}
            </div>
            <span v-else class="controller-info-no-data">정보 없음</span>
          </span>
        </div>
      </div>

      <!-- 최종 통신 시간 -->
      <div class="controller-info-item">
        <div class="controller-info-line">
          <i class="fa-regular fa-clock me-2"></i>
          <span class="controller-info-label">최근 사용:</span>
          <span class="controller-info-value">{{ formattedLastDate }}</span>
        </div>
      </div>
      </div>

      <!-- 푸터: 업데이트 버튼 -->
      <div class="controller-card-footer" v-if="shouldShowUpdateButton">
        <button
          type="button"
          class="btn btn-update-firmware"
          :disabled="currentStatus !== 2"
          @click="onFirmwareUpdateClick"
        >
          <i class="fa-regular fa-circle-down me-2"></i>
          펌웨어 업데이트
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, defineProps, computed, getCurrentInstance, watch } from 'vue';
import Card from '@/common/template/components/bootstrap/Card.vue';
import CardBody from '@/common/template/components/bootstrap/CardBody.vue';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import useMqttControl from '@/common/hooks/useMqttControl';
import ListGroupItem from '@/common/components/widgets/ListGroupItem.vue';
import ControllerStatus from '@/common/components/widgets/ControllerStatus.vue';
import { MQTTTempCommand } from '@/common/constants';
import moment from 'moment';
import { getSignalStrength, getSignalBars } from '@/common/utils/signalStrength';
import Axios, { CtrlApi } from '@/api';
import {
  resolveControllerDisplayName,
  formatControllerSerialWithName,
} from '@/common/utils/controllerDisplayName';
import { resolveControllerLastDate } from '@/common/utils/controllerLastDate';
import { isOldT01OtaLine } from '@/common/utils/controllerOta';

const mqttControl = reactive(useMqttControl());
const { emitter } = getCurrentInstance().appContext.config.globalProperties;
const router = useRouter();
const props = defineProps(['index', 'data']);
const store = useStore();
const getters = computed(() => store.getters);
const upgradeTempController = computed(
  () => getters.value['home/getUpgradeTempController'],
);
const isTempController = computed(() => {
  return props.data._id.split("_").length < 2;
});

// 컨트롤러 타입에 따른 아이콘 클래스
const controllerIconClass = computed(() => {
  return isTempController.value 
    ? 'fa-solid fa-thermometer-half' 
    : 'fa-regular fa-lightbulb';
});

let tempUpgradeTimeoutId = ref(undefined);
const currentStatus = ref(0); // Track current status for update button visibility
const lastResolvedStatus = ref(0); // 0/2/3 (조회중 1 제외) - 조회중일 때 이전 표시 유지용

// 유효한 버전인지 확인 (0, null, undefined가 아닌 경우)
const hasValidVersion = computed(() => {
  const version = props.data?.version;
  return version !== undefined && version !== null && version !== '' && Number(version) > 0;
});

const needsUpdate = computed(() => {
  if (!hasValidVersion.value) return false;
  if (!props.data.lastVersion) return false;
  return props.data.lastVersion > props.data.version;
});

// 업데이트 버튼 표시 조건
const shouldShowUpdateButton = computed(() => {
  // T01(구형): 버전 표시는 최신으로 맞추지만, Upgrade "1" 재전송을 위해 온라인만으로 표시
  if (isTempController.value && isOldT01OtaLine(props.data)) {
    return currentStatus.value === 2;
  }

  // T02 등: 업데이트 필요 + 온라인일 때만 (최신 버전이면 숨김)
  if (isTempController.value) {
    return needsUpdate.value && currentStatus.value === 2;
  }

  // 조명제어기: 업데이트가 필요하면 온라인/오프라인 관계없이 표시
  return needsUpdate.value;
});

const modelName = computed(() => {
  const products = getters.value['home/getProducts'] || [];
  return resolveControllerDisplayName(props.data, products, '조명제어기');
});

/** 이 컨트롤러의 디바이스가 포함된 방(그룹) 이름 — 온도조절기는 보통 1개, 조명제어기는 복수 가능 */
const roomNames = computed(() => {
  const controllerId = props.data?._id;
  if (!controllerId) return [];

  const zones = getters.value['home/getZones'] || [];
  const names = [];
  const seen = new Set();

  for (const zone of zones) {
    for (const group of zone.groups || []) {
      const hasDevice = (group.devices || []).some(
        (d) => d.controllerId === controllerId,
      );
      if (!hasDevice || !group.name || seen.has(group.name)) continue;
      seen.add(group.name);
      names.push(group.name);
    }
  }
  return names;
});

// RSSI 신호세기 변환
const signalStrength = computed(() => {
  const rssi = props.data?.rssi;
  console.log('[UpdateControllerItem] RSSI 값:', rssi, '타입:', typeof rssi);
  const result = getSignalStrength(rssi);
  console.log('[UpdateControllerItem] 신호세기 변환 결과:', result);
  return result;
});

// RSSI 막대 개수 계산
const signalBars = computed(() => {
  return getSignalBars(props.data?.rssi);
});

// RSSI 데이터 존재 여부 확인
const hasRssi = computed(() => {
  const rssi = props.data?.rssi;
  const hasValue = rssi !== null && rssi !== undefined && !isNaN(rssi);
  console.log('[UpdateControllerItem] hasRssi:', hasValue, 'rssi:', rssi);
  return hasValue;
});

// WiFi 신호세기 "알 수 없음" 표시 여부 (조회중일 때는 직전 확정 상태 기준으로 이전 표시 유지)
const showSignalUnknown = computed(() => {
  if (currentStatus.value === 0 || currentStatus.value === 3) return true;
  if (currentStatus.value === 1) return lastResolvedStatus.value === 0 || lastResolvedStatus.value === 3;
  return false;
});

// SSID 데이터 존재 여부 확인
const hasSsid = computed(() => {
  const ssid = props.data?.ssid;
  const hasValue = ssid !== null && ssid !== undefined && typeof ssid === 'string' && ssid.length > 0;
  console.log('[UpdateControllerItem] hasSsid:', hasValue, 'ssid:', ssid);
  return hasValue;
});

// 최근 사용(admin lastDate와 동일): device.updatedAt 최댓값
const formattedLastDate = computed(() => {
  const lastDate = resolveControllerLastDate(
    props.data?._id,
    getters.value['home/getZones'] || [],
    props.data?.lastDate,
  );
  if (!lastDate) return '정보 없음';
  return moment(lastDate).format('YYYY-MM-DD HH:mm:ss');
});

// updatedAt 데이터 존재 여부 확인 (레거시 로그용)
const hasUpdatedAt = computed(() => {
  const updatedAt = props.data?.updatedAt;
  const hasValue = updatedAt !== null && updatedAt !== undefined;
  console.log('[UpdateControllerItem] hasUpdatedAt:', hasValue, 'updatedAt:', updatedAt);
  return hasValue;
});

// 컴포넌트 마운트 시 전체 데이터 로그
onMounted(() => {
  console.log('[UpdateControllerItem] 컴포넌트 마운트됨');
  console.log('[UpdateControllerItem] 전체 props.data:', JSON.stringify(props.data, null, 2));
  console.log('[UpdateControllerItem] props.data 키 목록:', Object.keys(props.data || {}));
  console.log('[UpdateControllerItem] rssi:', props.data?.rssi);
  console.log('[UpdateControllerItem] updatedAt:', props.data?.updatedAt);
  console.log('[UpdateControllerItem] createdAt:', props.data?.createdAt);
});

const updateController = async () => {
  if (!props.data?._id) return;
  const result = await Axios.get(CtrlApi.CTRL_INFO.replace(':id', props.data._id));
  console.log('[UpdateControllerItem] 컨트롤러 조회 result:', result);
  if (result?.resultData) {
    store.commit('home/updateController', result.resultData);
  }
};

// Handle status changes from ControllerStatus component
const onStatusChanged = async (payload) => {
  currentStatus.value = payload.status;
  if (payload.status !== 1) lastResolvedStatus.value = payload.status;
  await updateController();
};

// props.data 변화 감시
watch(() => props.data, (newData, oldData) => {
  console.log('[UpdateControllerItem] props.data 변경 감지');
  console.log('[UpdateControllerItem] 이전 데이터:', oldData);
  console.log('[UpdateControllerItem] 새로운 데이터:', newData);
  console.log('[UpdateControllerItem] rssi 변경:', oldData?.rssi, '->', newData?.rssi);
  console.log('[UpdateControllerItem] updatedAt 변경:', oldData?.updatedAt, '->', newData?.updatedAt);
}, { deep: true, immediate: true });

watch(upgradeTempController, () => {
  console.log("watch upgradeTempController", upgradeTempController.value);

  if (!upgradeTempController.value?.value) {
    return;
  }

  if (upgradeTempController.value?.value?.Upgrade?.indexOf("Version") > -1) {
    //console.log(upgradeTempController.value?.value?.Upgrade);
  } else {
    emitter.emit('hideLoading');

    if (upgradeTempController.value?.value?.Upgrade?.indexOf("Successful") > -1) {
      emitter.emit('showPopAlert', {
        description: '펌웨어 업데이트에 성공 했습니다.',
      });
    } else {
      emitter.emit('showPopAlert', {
        description: '펌웨어 업데이트에 실패 했습니다.',
      });
    }

    store.commit("home/setUpgradeTempController", undefined);
    clearTimeout(tempUpgradeTimeoutId);
    tempUpgradeTimeoutId = undefined;
  }
});


// 펌웨어 업데이트 클릭 핸들러
const onFirmwareUpdateClick = () => {
  if (isTempController.value) {
    // 온도조절기 업데이트
    onTempControllerUpdateClick();
  } else {
    // 조명제어기(MTZ) 업데이트
    onLightControllerUpdateClick();
  }
};

// 온도조절기 펌웨어 업데이트
const onTempControllerUpdateClick = () => {
  // T01(항상) / 모델 미확정·T02 10101 미만: 기기 고정 OTA용 Upgrade "1"
  if (props.data.useLegacyUpgrade1) {
    emitter.emit('showLoading');
    mqttControl.onPublishTemp(props.data._id, MQTTTempCommand.Upgrade, '1');

    tempUpgradeTimeoutId = setTimeout(() => {
      emitter.emit('hideLoading');
      tempUpgradeTimeoutId = undefined;
      store.commit('home/setUpgradeTempController', undefined);

      emitter.emit('showPopAlert', {
        description: '펌웨어 업데이트에 실패 했습니다.',
      });
    }, 30000);
    return;
  }

  if (!props.data.lastVersion || !props.data.downloadUrl) {
    emitter.emit('showPopAlert', {
      description: '펌웨어 정보가 없습니다.',
    });
    return;
  }

  emitter.emit('showLoading');

  const firmwareUrl =
    process.env.VUE_APP_PROXY_FIRMWARE_DOMAIN + `/api/${props.data.downloadUrl}`;

  mqttControl.onPublishTemp(props.data._id, MQTTTempCommand.Upgrade, {
    firmware_url: firmwareUrl,
    new_version: props.data.lastVersion,
  });

  tempUpgradeTimeoutId = setTimeout(() => {
    emitter.emit('hideLoading');
    tempUpgradeTimeoutId = undefined;
    store.commit("home/setUpgradeTempController", undefined);

    emitter.emit('showPopAlert', {
      description: '펌웨어 업데이트에 실패 했습니다.',
    });
  }, 30000);
};

// 조명제어기(MTZ) 펌웨어 업데이트
const onLightControllerUpdateClick = () => {
  if (!props.data.lastVersion || !props.data.downloadUrl) {
    emitter.emit('showPopAlert', {
      description: '펌웨어 정보가 없습니다.',
    });
    return;
  }

  emitter.emit('showPopConfirm', {
    description: '펌웨어를 업데이트 하시겠습니까?',
    okCallback: () => {
      emitter.emit('showLoading');

      const firmwareUrl = process.env.VUE_APP_PROXY_FIRMWARE_DOMAIN + `/api/${props.data.downloadUrl}`;

      mqttControl.onPublishFirmware(props.data._id, {
        serial: props.data._id,
        cmd: 'f',
        type: 1,
        new_version: props.data.lastVersion,
        firmware_url: firmwareUrl,
      });

      // 진행률(PopProgress)이 AppUpdatePage에서 표시되므로 시작 알림은 생략
      setTimeout(() => {
        emitter.emit('hideLoading', true);
      }, 1000);
    },
  });
};
</script>

<style scoped>
.controller-card {
  background-color: #ffffff;
  border: 1px solid #e9ecef;
  border-radius: 1.2rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  overflow: hidden;
}

.controller-card:hover {
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  transform: translateY(-2px);
}

/* 헤더 */
.controller-card-header {
  padding: 1rem 1.25rem;
  border-bottom: 1px solid #f0f0f0;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  background-color: #f8f9fa;
}

.controller-header-left {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  flex: 1;
  min-width: 0;
}

.controller-icon {
  color: #0d6efd;
  font-size: 1.1rem;
  margin-top: 0.15rem;
  flex-shrink: 0;
}

.controller-title-block {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  min-width: 0;
}

.controller-model {
  font-size: 1rem;
  font-weight: 600;
  color: #212529;
  line-height: 1.3;
}

.controller-serial {
  font-size: 0.8rem;
  font-weight: 500;
  color: #6c757d;
  line-height: 1.3;
  word-break: break-all;
}

.controller-room-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
}

.controller-room-tag {
  display: inline-flex;
  align-items: center;
  padding: 0.15rem 0.5rem;
  font-size: 0.75rem;
  font-weight: 500;
  line-height: 1.3;
  color: #0d6efd;
  background-color: #ffffff;
  border: 1px solid #0d6efd;
  border-radius: 0.35rem;
  white-space: nowrap;
}

.controller-header-right {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-shrink: 0;
  margin-top: 0.1rem;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  font-size: 0.75rem;
  font-weight: 600;
  padding: 0.25rem 0.75rem;
  border-radius: 1rem;
  white-space: nowrap;
}

.status-badge-update {
  background: linear-gradient(135deg, #dc3545 0%, #c82333 100%);
  color: #ffffff;
  box-shadow: 0 2px 4px rgba(220, 53, 69, 0.3);
}

.status-badge-current {
  background: linear-gradient(135deg, #28a745 0%, #218838 100%);
  color: #ffffff;
  box-shadow: 0 2px 4px rgba(40, 167, 69, 0.3);
}

.status-badge-inline {
  margin-left: 0.5rem;
  flex-shrink: 0;
}

/* 본문 */
.controller-card-body {
  padding: 1rem 1.25rem;
}

.controller-info-item {
  padding: 0.75rem 0;
  border-bottom: 1px solid #f0f0f0;
}

.controller-info-item:last-child {
  border-bottom: none;
}

.controller-info-line {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: nowrap;
  overflow: hidden;
}

.controller-info-label {
  font-size: 0.8rem;
  color: #6c757d;
  font-weight: 500;
  white-space: nowrap;
  flex-shrink: 0;
  text-align: left;
}

.controller-info-value {
  font-size: 0.9rem;
  font-weight: 600;
  color: #212529;
  word-break: break-all;
  text-align: left;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
  min-width: 0;
}

.controller-info-no-data {
  font-size: 0.85rem;
  color: #6c757d;
  font-weight: 500;
  font-style: italic;
}

/* 버전 그룹 */
.controller-version-group {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: nowrap;
  overflow-x: auto;
  white-space: nowrap;
  flex-shrink: 0;
}

.version-item-inline {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  white-space: nowrap;
  flex-shrink: 0;
}

.version-label-inline {
  font-size: 0.75rem;
  color: #6c757d;
  font-weight: 500;
  white-space: nowrap;
}

.version-value-inline {
  font-size: 0.875rem;
  font-weight: 600;
  color: #212529;
  padding: 0.25rem 0.625rem;
  background-color: #f8f9fa;
  border-radius: 0.5rem;
  white-space: nowrap;
  min-width: fit-content;
}

.version-value-inline.version-value-update {
  background: linear-gradient(135deg, #fff5f5 0%, #ffe5e5 100%);
  color: #dc3545;
}

.version-arrow {
  color: #6c757d;
  font-size: 0.875rem;
  flex-shrink: 0;
  white-space: nowrap;
}

/* 신호세기 컨테이너 */
.signal-strength-container {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
}

/* 신호 막대 컨테이너 */
.signal-bars {
  display: flex;
  align-items: flex-end;
  gap: 2px;
  height: 1.2rem;
}

/* 신호 막대 */
.signal-bar {
  width: 3px;
  border-radius: 2px 2px 0 0;
  transition: all 0.2s ease;
  background-color: #e0e0e0;
}

.signal-bar:nth-child(1) {
  height: 0.3rem;
}

.signal-bar:nth-child(2) {
  height: 0.4rem;
}

.signal-bar:nth-child(3) {
  height: 0.5rem;
}

.signal-bar:nth-child(4) {
  height: 0.6rem;
}

.signal-bar:nth-child(5) {
  height: 0.7rem;
}

.signal-bar:nth-child(6) {
  height: 0.8rem;
}

.signal-bar:nth-child(7) {
  height: 0.9rem;
}

.signal-bar:nth-child(8) {
  height: 1rem;
}

.signal-bar:nth-child(9) {
  height: 1.1rem;
}

.signal-bar:nth-child(10) {
  height: 1.2rem;
}

.signal-bar-active {
  background-color: #28a745;
}

/* 신호세기 배지 */
.signal-strength-badge {
  display: inline-flex;
  align-items: center;
  font-size: 0.75rem;
  font-weight: 600;
  padding: 0.25rem 0.75rem;
  border-radius: 1rem;
  border: 1px solid;
  white-space: nowrap;
  transition: all 0.2s ease;
}

.signal-strength-badge:hover {
  opacity: 0.9;
  transform: scale(1.05);
}

/* 푸터 */
.controller-card-footer {
  padding: 1rem 1.25rem;
  border-top: 1px solid #f0f0f0;
  background-color: #f8f9fa;
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.btn-update-firmware {
  width: 100%;
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
  font-weight: 600;
  border-radius: 0.5rem;
  background: linear-gradient(135deg, #0d6efd 0%, #0a58ca 100%);
  color: #ffffff;
  border: none;
  box-shadow: 0 2px 4px rgba(13, 110, 253, 0.3);
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
}

.btn-update-firmware:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(13, 110, 253, 0.4);
}

.btn-update-firmware:disabled {
  background: linear-gradient(135deg, #6c757d 0%, #5a6268 100%);
  color: #ffffff;
  box-shadow: 0 2px 4px rgba(108, 117, 125, 0.2);
  cursor: not-allowed;
  opacity: 1;
}

/* 모바일 반응형 */
@media (max-width: 768px) {
  .controller-card-header {
    padding: 0.875rem 1rem;
    flex-wrap: wrap;
    gap: 0.5rem;
  }

  .controller-card-body {
    padding: 0.875rem 1rem;
  }

  .controller-card-footer {
    padding: 0.875rem 1rem;
    flex-wrap: wrap;
  }

  .controller-info-line {
    flex-wrap: nowrap;
    gap: 0.375rem;
    overflow: hidden;
  }

  .controller-info-label {
    font-size: 0.75rem;
    flex-shrink: 0;
  }

  .controller-info-value {
    font-size: 0.85rem;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    flex: 1;
    min-width: 0;
  }

  .controller-version-group {
    flex-wrap: nowrap;
    gap: 0.375rem;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    flex: 1;
    min-width: 0;
    margin-left: 0;
    margin-top: 0;
  }

  .version-item-inline {
    font-size: 0.8rem;
  }

  .version-label-inline {
    font-size: 0.7rem;
  }

  .version-value-inline {
    font-size: 0.8rem;
    padding: 0.2rem 0.5rem;
  }

  .version-arrow {
    flex-shrink: 0;
  }

  .btn-update-firmware {
    width: 100%;
    justify-content: center;
  }
}
</style>
